import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import update from 'immutability-helper';

// PrimeNG
import { EditorModule } from 'primeng/editor';
import { Skeleton } from 'primeng/skeleton';
import { Button } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Tree, TreeNodeExpandEvent } from 'primeng/tree';
import { MessageService, TreeNode } from 'primeng/api';

import { DocumentService } from '../../../services/service';
import { finalize, tap } from 'rxjs';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FileSelectionProps } from './file-selection';
import { NodesCacheService } from './nodes-cache.service';
import { FileIconComponent } from '../../../shared/components/file-icon/file-icon.component';

@Component({
    selector: 'app-file-selection',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        Tree,
        Button,
        Skeleton,
        DialogModule,
        EditorModule,
        ConfirmDialogModule,
        FileIconComponent
    ],
    templateUrl: 'file-selection.component.html',
    styleUrl: 'file-selection.component.scss',
    providers: [DocumentService]
})
export class FileSelectionComponent implements OnInit {
    configuration: FileSelectionProps;
    treeNodes: TreeNode[] = [];
    selectedNodes: TreeNode[] = [];
    loading: boolean = true;
    loadingData = new Array(5);

    constructor(private readonly documentService: DocumentService,
        public ref: DynamicDialogRef,
        public config: DynamicDialogConfig<FileSelectionProps>,
        public messageService: MessageService,
        public nodesCacheService: NodesCacheService
    ) {
        this.configuration = config.data!;
    }

    ngOnInit(): void {
        let needReload = this.nodesCacheService.needReload(this.configuration.entityType, this.configuration.entityId);
        if (needReload) {
            this.loadRootNodes().subscribe();
        }
        else {
            this.treeNodes = this.nodesCacheService.getNodes();
            this.loading = false;
        }
    }

    loadRootNodes() {
        return this.getDocuments()
            .pipe(
                tap(data => {
                    let nodes = this.transformToTreeNodes(data.items);
                    if (data.nextLink) {
                        nodes.push(this.createLoadMoreNode("load-root", data.nextLink, ''));
                    }
                    this.treeNodes = update(this.treeNodes, { $set: [...nodes] });
                    this.nodesCacheService.setNodes(this.treeNodes, this.configuration.entityType, this.configuration.entityId);
                }),
                finalize(() => this.loading = false)
            );
    }

    onExpandFolder(event: TreeNodeExpandEvent) {
        if (event.node.data?.isLoadMore) {
            this.loadMore(event);
        }
        else if (!event.node.children) {
            const folderId = event.node.data.id;            
            const folderUrl = event.node.data.url;
            event.node.loading = true;
            this.getDocuments(folderUrl)
                .subscribe((data) => {
                    let _node = { ...event.node };
                    _node.children = []
                    _node.children = this.transformToTreeNodes(data.items);
                    _node.loading = false;

                    if (data.nextLink) {
                        _node.children.push(this.createLoadMoreNode("load-" + _node.key, data.nextLink, _node.key, folderUrl));
                    }
                    let index = this.treeNodes.findIndex(t => t.key == folderId);
                    this.treeNodes = update(this.treeNodes, { [index]: { $set: _node } });
                    this.nodesCacheService.setNodes(this.treeNodes, this.configuration.entityType, this.configuration.entityId);
                });
        }
    }

    loadMore(event: TreeNodeExpandEvent) {       
        const { folderId, url, nextLink } = event.node.data;

        event.node.loading = true;
        this.getDocuments(url, nextLink)
            .subscribe((data) => {
                let _node = { ...event.node.parent };
                let newItems = this.transformToTreeNodes(data.items);

                if (!event.node.parent) {
                    let children = [...this.treeNodes];
                    let loadMore = children.pop()!;
                    let hasNextLink = !!data.nextLink;
                    if (hasNextLink) {
                        loadMore = update(loadMore, { loading: { $set: false }, expanded: { $set: false }, data: { nextLink: { $set: data.nextLink } } });
                        children.push(loadMore);
                    }
                    this.treeNodes = update(children, { $splice: [[this.treeNodes.length - 1, hasNextLink ? 0 : 1, ...newItems]] });
                    this.nodesCacheService.setNodes(this.treeNodes, this.configuration.entityType, this.configuration.entityId);
                }
                else {
                    let children = _node.children!;
                    let loadMore = children.pop()!;
                    children = update(children, { $push: newItems });

                    if (data.nextLink) {
                        loadMore = update(loadMore, { loading: { $set: false }, expanded: { $set: false }, data: { nextLink: { $set: data.nextLink } } });
                        children.push(loadMore);
                    }

                    _node.children = children;
                    _node.loading = false;

                    let index = this.treeNodes.findIndex(t => t.key == folderId);
                    this.treeNodes = update(this.treeNodes, { [index]: { $set: _node } });
                    this.nodesCacheService.setNodes(this.treeNodes, this.configuration.entityType, this.configuration.entityId);
                }
                event.node.loading = false;
            });
    }

    onClose() {
        this.ref.close();
    }

    onAdd() {
        let selectedURLs = (this.selectedNodes.length ? this.selectedNodes : [this.selectedNodes as TreeNode<any>])
            .filter(s => !s.data.isLoadMore && !!s.data.url).map(s => s.data.url);
        const { onAdd, manualClose } = this.configuration;

        if (onAdd) onAdd(selectedURLs);
        if (!manualClose) this.ref.close();
    }

    onRefresh() {
        this.loading = true;
        this.loadRootNodes().subscribe();
    }

    getDocuments(folderId?: string, nextLink?: string) {
        return this.documentService.getDocuments(this.configuration.entityType, this.configuration.entityId, folderId, nextLink,undefined,10);
    }

    transformToTreeNodes(items: any[]): TreeNode[] {
        return items.map((item) => {
            // extension test without the backtracking regex: last dot after last separator, not final
            const sep = Math.max(item.name.lastIndexOf('/'), item.name.lastIndexOf('\\'));
            const dot = item.name.lastIndexOf('.');
            let isFile = dot > sep && dot < item.name.length - 1;
            return {
                key: item.id,
                label: item.name,
                selectable: isFile,
                data: { id: item.id, isFile: isFile, url: item.webUrl },
                leaf: isFile
            };
        });
    }

    createLoadMoreNode(key: string, nextLink?: string, folderId?: string, url?: string) {
        return {
            key: key,
            label: 'Load more...',
            styleClass: 'load-more',
            selectable: false,
            expanded: false,
            leaf: false,
            data: { isLoadMore: true, nextLink: nextLink, folderId: folderId, url: url, icon: 'pi pi-refresh' },
        };
    }
}
